import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Fingerprint, User, Sparkles, Rocket, LineChart, Briefcase, Mic } from 'lucide-react';

// ─── Data ────────────────────────────────────────────────────────────────────

const PROOF_GALLERY = [
  {
    label: 'Database migration',
    input: 'Finished the Firebase → Postgres migration at 2 AM. 4M rows. Read costs were compounding at 18% MoM because of multi-tenant filtering. Moved to managed Postgres with proper indexing on tenant_id. Done.',
    output: 'We moved 4 million rows off Firebase in 12 hours.\n\nThe bottleneck: read costs compounding at 18% MoM due to multi-tenant filtering requirements.\n\nThe fix: a managed Postgres instance with proper indexing on tenant_id. No vector DB. No architectural overhaul. Just the right index on the right column.',
  },
  {
    label: 'Stripe integration',
    input: 'Webhooks kept failing silently. Spent 3 days on it. Root cause: we were validating the raw body after Express had already parsed it. Added rawBody middleware before the JSON parser. Fixed.',
    output: 'Our Stripe webhooks were failing silently for three days.\n\nThe cause: Express was parsing the request body before our webhook validator could read the raw bytes Stripe requires for signature verification.\n\nThe fix: a single rawBody middleware added before the JSON parser. Three days of debugging resolved in four lines.',
  },
  {
    label: 'Onboarding drop-off',
    input: '62% of users drop off at step 3 of onboarding (the "connect your project" step). Hypothesis: the empty state is too abstract. Testing a pre-filled demo project instead.',
    output: '62% of new users stop at step 3 of our onboarding flow.\n\nThe pattern: the "connect your project" screen shows an empty state with no example. Users with no existing notes have nothing to connect.\n\nThe test: a pre-filled demo project that shows the output before asking for the input. Running this week.',
  },
];

const PERSONAS = [
  {
    icon: <Rocket size={16} className="text-slate-600" />,
    name: 'Pragmatic founders',
    desc: 'Daily work turned into a consistent public presence — without breaking your build cadence.',
  },
  {
    icon: <LineChart size={16} className="text-slate-600" />,
    name: 'Bootstrappers',
    desc: 'Your shipping history is already your strongest marketing asset. Buildlog brings it to the surface.',
  },
  {
    icon: <Briefcase size={16} className="text-slate-600" />,
    name: 'Agency owners',
    desc: 'Client wins turned into authoritative case studies that close the next engagement.',
  },
  {
    icon: <Mic size={16} className="text-slate-600" />,
    name: 'Creator-educators',
    desc: 'Raw thinking shaped into newsletters and curriculum — without the blank page.',
  },
];

const ENGINE_LAYERS = [
  {
    step: '01',
    title: 'Brand Voice',
    body: 'Lock your vocabulary and tone once. Every post Buildlog shapes is guaranteed to sound like you — not a generic model averaging the internet.',
    outcome: 'You stop sounding like a ChatGPT wrapper.',
  },
  {
    step: '02',
    title: 'Persona',
    body: 'Define who is speaking and who is reading. Your lens, your authority, your exact market position — remembered permanently.',
    outcome: 'Your insights land with authority, not noise.',
  },
  {
    step: '03',
    title: 'Framework',
    body: 'Give the engine a proven structure. Messy notes go in — a complete, well-argued post comes out. Automatically.',
    outcome: 'Rough notes become undeniable arguments.',
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export function Landing() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Buildlog — Your work, published';
  }, []);

  return (
    <div className="bg-white text-slate-900 font-sans">

      {/* ── HERO ── */}
      <section className="min-h-[88vh] flex flex-col justify-center items-center px-6 pt-28 pb-20 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.025]"
          style={{
            backgroundImage: `url('/fingerprint-pattern.svg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'top center',
          }}
        />
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-white/60 to-white" />

        <div className="relative z-10 max-w-4xl mx-auto">
          <p
            className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-7 flex items-center justify-center gap-3"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          >
            <span className="w-8 h-px bg-slate-200 inline-block" />
            Your work, published
            <span className="w-8 h-px bg-slate-200 inline-block" />
          </p>

          <h1 className="text-[clamp(40px,6.5vw,84px)] font-semibold leading-[1.06] tracking-tight text-slate-900 mb-6">
            Stop inventing marketing.<br />
            <span className="text-slate-400 font-light">Start documenting reality.</span>
          </h1>

          <p className="text-[clamp(16px,1.8vw,19px)] text-slate-500 font-light leading-relaxed max-w-2xl mx-auto mb-10">
            Your daily work already contains everything you need to build authority. Buildlog takes your raw notes, applies your voice and structure, and turns what you actually built into posts that earn trust — with readers and with Answer Engines.
          </p>

          <div className="flex gap-3 flex-wrap justify-center">
            <button
              onClick={() => navigate('/login')}
              className="px-7 py-3.5 bg-slate-900 text-white font-semibold text-[15px] rounded-full hover:bg-slate-700 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
            >
              Start building in public
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-7 py-3.5 border border-slate-200 text-slate-600 font-medium text-[15px] rounded-full hover:border-slate-900 hover:text-slate-900 transition-all flex items-center gap-2"
            >
              <Fingerprint size={15} /> See it in action
            </button>
          </div>
        </div>
      </section>

      {/* ── PROOF GALLERY ── */}
      <section className="px-6 py-24 bg-slate-50 border-t border-slate-100">
        <div className="max-w-5xl mx-auto">
          <div className="mb-14">
            <p
              className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-5"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              Signal Extraction
            </p>
            <h2 className="text-[clamp(26px,3.8vw,48px)] font-medium leading-tight tracking-tight text-slate-900 mb-5">
              From rough notes<br />
              <span className="text-slate-400 font-light">to published authority.</span>
            </h2>
            <p className="text-[15px] text-slate-500 font-light leading-relaxed max-w-xl">
              Three real examples. Your raw notes, chat transcripts, or 2 AM commits on the left — what Buildlog turns them into on the right. The facts are yours. The structure is Buildlog's.
            </p>
          </div>

          <div className="space-y-6">
            {PROOF_GALLERY.map(({ label, input, output }) => (
              <div
                key={label}
                className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-sm"
              >
                {/* Raw input */}
                <div className="p-6 border-b md:border-b-0 md:border-r border-slate-100">
                  <p
                    className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 mb-4"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  >
                    Raw notes — {label}
                  </p>
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                      <User size={13} className="text-slate-500" />
                    </div>
                    <p className="text-[14px] text-slate-500 font-light leading-relaxed">{input}</p>
                  </div>
                </div>

                {/* Shaped output */}
                <div className="p-6 bg-white">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-lg bg-[#D97757] flex items-center justify-center shrink-0">
                      <Sparkles size={14} className="text-white fill-white" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Fingerprint size={12} className="text-slate-400" />
                      <p
                        className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400"
                        style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                      >
                        Proof of Work
                      </p>
                    </div>
                  </div>
                  <div className="text-[14px] text-slate-800 leading-relaxed space-y-2 border-l-2 border-slate-900 pl-4">
                    {output.split('\n\n').map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE ANSWER ENGINE ERA ── */}
      <section className="px-6 py-24 bg-white border-t border-slate-100">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
          <div>
            <p
              className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-5"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              The Answer Engine Era
            </p>
            <h2 className="text-[clamp(26px,3.8vw,48px)] font-medium leading-tight tracking-tight text-slate-900 mb-6">
              The Answer Engine era<br />
              <span className="text-slate-400 font-light">is already here.</span>
            </h2>
            <p className="text-[15px] text-slate-500 font-light leading-relaxed mb-5">
              Perplexity, ChatGPT, and Gemini don't rank pages — they surface sources. The sources they cite share one property: specific, proprietary facts that exist nowhere else on the internet.
            </p>
            <p className="text-[15px] text-slate-500 font-light leading-relaxed">
              Generic advice doesn't qualify. But the exact index that fixed your multi-tenant read cost? The four-line middleware fix that took three days to find? Those facts are yours. No model trained on the internet has them. Buildlog turns them into the kind of posts that get cited — by AI and by the people AI is advising.
            </p>
          </div>

          <div className="space-y-0 border border-slate-200 rounded-2xl overflow-hidden">
            {[
              {
                label: 'Information Gain',
                body: 'Publishing what you actually built — the specific problems, the real fixes — gives Answer Engines facts they cannot find anywhere else.',
                payoff: 'Your posts become the primary source, not a summary of someone else\'s.',
              },
              {
                label: 'Entity Coherence',
                body: 'When your voice and point of view are consistent across every post, AI systems form a clear picture of who you are and what you stand for.',
                payoff: 'You become the recognised expert in your category — not just another voice.',
              },
              {
                label: 'Narrative Control',
                body: 'Your voice rules are applied to every post, automatically. Claude shapes the structure — you own every word of substance.',
                payoff: 'Every post sounds like you on your best day, without the two hours of rewriting.',
              },
            ].map(({ label, body, payoff }, i) => (
              <div
                key={label}
                className={`p-7 ${i < 2 ? 'border-b border-slate-100' : ''}`}
              >
                <p
                  className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-900 mb-2 font-medium"
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                >
                  {label}
                </p>
                <p className="text-[14px] text-slate-500 font-light leading-relaxed mb-3">{body}</p>
                <p className="text-[13px] text-slate-800 font-medium">{payoff}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE CREATIVE DIRECTOR ── */}
      <section className="px-6 py-24 bg-slate-50 border-t border-slate-100">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-2xl mb-16">
            <p
              className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-5"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              The Narrative Engine
            </p>
            <h2 className="text-[clamp(26px,3.8vw,48px)] font-medium leading-tight tracking-tight text-slate-900 mb-6">
              Your AI Creative Director.
            </h2>
            <p className="text-[15px] text-slate-500 font-light leading-relaxed">
              Three rules you set once. Applied to every post, automatically — so every output sounds like you, not like a model that guessed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ENGINE_LAYERS.map(({ step, title, body, outcome }) => (
              <div
                key={step}
                className="p-7 bg-white border border-slate-200 rounded-2xl flex flex-col gap-5"
              >
                <div>
                  <p
                    className="text-[10px] font-mono text-slate-400 mb-3 tracking-[0.15em]"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  >
                    {step}
                  </p>
                  <h3 className="font-semibold text-[17px] text-slate-900 mb-3">{title}</h3>
                  <p className="text-[13px] text-slate-500 font-light leading-relaxed">{body}</p>
                </div>
                <div className="mt-auto pt-5 border-t border-slate-100">
                  <p className="text-[13px] text-slate-800 font-medium">"{outcome}"</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="px-6 py-24 bg-white border-t border-slate-100">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-2xl mb-16">
            <p
              className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-5"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              The Workflow
            </p>
            <h2 className="text-[clamp(26px,3.8vw,48px)] font-medium leading-tight tracking-tight text-slate-900">
              Drop in your notes.<br />
              <span className="text-slate-400 font-light">Publish your best thinking.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100 border border-slate-100 rounded-2xl overflow-hidden">
            {[
              {
                step: 'Archive',
                title: 'Brain dump everything',
                body: 'No formatting required. Drop in daily progress notes, paste a Claude conversation where you worked through the architecture, or import years of markdown journals. Buildlog organises what you have already produced.',
              },
              {
                step: 'Route',
                title: 'Your rules take over',
                body: 'Select your project. Your voice, your point of view, and your chosen structure are applied automatically — Claude gets a precise brief built entirely from your own rules.',
                accent: true,
              },
              {
                step: 'Deploy',
                title: 'Publish in seconds',
                body: 'Claude shapes high-signal posts ready for LinkedIn, X, or your newsletter — directly from your archived notes. Your thinking, structured and ready to ship.',
              },
            ].map(({ step, title, body, accent }) => (
              <div key={step} className="p-8 bg-white">
                <p
                  className={`text-[10px] font-mono uppercase tracking-[0.18em] mb-4 ${accent ? 'text-slate-900 font-medium' : 'text-slate-400'}`}
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                >
                  {step}
                </p>
                <h3 className="font-semibold text-[17px] text-slate-900 mb-3">{title}</h3>
                <p className="text-[14px] text-slate-500 font-light leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO ── */}
      <section className="px-6 py-24 bg-slate-50 border-t border-slate-100">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
          <div>
            <p
              className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-5"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              Who it's for
            </p>
            <h2 className="text-[clamp(24px,3.5vw,42px)] font-medium leading-tight tracking-tight text-slate-900 mb-6">
              Built for founders<br />whose work is the proof.
            </h2>
            <p className="text-[15px] text-slate-500 font-light leading-relaxed mb-4">
              You know that building in public compounds. You know that audience equals leverage. You are already doing the work that makes for the best possible content — shipping fast, debugging real problems, learning things no one else has written down yet.
            </p>
            <p className="text-[15px] text-slate-500 font-light leading-relaxed">
              Buildlog is the bridge between the work you are doing and the authority you are building. It takes what is already in your notes and turns it into posts worth reading — without touching your flow state.
            </p>
          </div>

          <ul className="flex flex-col gap-3">
            {PERSONAS.map(({ icon, name, desc }) => (
              <li
                key={name}
                className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                  {icon}
                </div>
                <div>
                  <p className="font-semibold text-[13px] text-slate-900 mb-0.5">{name}</p>
                  <p className="text-[13px] text-slate-400 leading-relaxed">{desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── CTA BAND ── */}
      <section className="px-6 py-28 border-t border-slate-900 bg-slate-900 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage: `url('/fingerprint-pattern.svg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10 max-w-lg mx-auto">
          <Fingerprint size={44} className="mx-auto text-white mb-7 opacity-60" />
          <h2 className="text-[clamp(26px,4vw,50px)] font-semibold tracking-tight text-white mb-4 leading-tight">
            Your work is already<br />worth reading.
          </h2>
          <p className="text-[15px] text-slate-400 font-light mb-10 leading-relaxed">
            Every commit, every fix, every lesson — already the makings of the authority you are building. Free account. Up and running in under two minutes.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-8 py-3.5 bg-white text-slate-900 font-semibold text-[15px] rounded-full hover:bg-slate-100 transition-all shadow-md hover:-translate-y-0.5"
          >
            Try it free
          </button>
        </div>
      </section>

    </div>
  );
}